import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateTrip } from '../hooks/useTrips';
// EmptyState import — reserved for future use

export function CreateTripPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const createTrip = useCreateTrip();

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Trip name is required';
    if (!startDate) newErrors.startDate = 'Start date is required';
    if (!endDate) newErrors.endDate = 'End date is required';
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    createTrip.mutate(
      { name: name.trim(), startDate, endDate },
      {
        onSuccess: (data) => navigate(`/trip/${data.id}`),
      }
    );
  };

  return (
    <div className="animate-fade-in max-w-lg mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/trips')}
          className="text-sm text-disney-blue-600 dark:text-disney-blue-400 hover:underline mb-2 inline-block"
        >
          &larr; Back to trips
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Trip</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-surface-dark-card rounded-xl p-6 shadow-card space-y-4">
        <div>
          <label htmlFor="trip-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Trip Name
          </label>
          <input
            id="trip-name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Disney World 2026"
            className={`w-full rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-surface-dark-card px-4 py-2 text-sm focus:border-disney-blue-500 focus:outline-none focus:ring-1 focus:ring-disney-blue-500`}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Start Date
          </label>
          <input
            id="start-date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className={`w-full rounded-lg border ${errors.startDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-surface-dark-card px-4 py-2 text-sm focus:border-disney-blue-500 focus:outline-none focus:ring-1 focus:ring-disney-blue-500`}
          />
          {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>}
        </div>

        <div>
          <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            End Date
          </label>
          <input
            id="end-date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className={`w-full rounded-lg border ${errors.endDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-surface-dark-card px-4 py-2 text-sm focus:border-disney-blue-500 focus:outline-none focus:ring-1 focus:ring-disney-blue-500`}
          />
          {errors.endDate && <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>}
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={createTrip.isPending}
            className="flex-1 px-4 py-2.5 bg-disney-blue-600 text-white rounded-xl font-medium hover:bg-disney-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createTrip.isPending ? 'Creating...' : 'Create Trip'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/trips')}
            className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>

      {createTrip.isError && (
        <p className="text-red-500 text-sm mt-3 text-center">{createTrip.error?.message}</p>
      )}
    </div>
  );
}
